import React, { useEffect, useState } from 'react';
import EditEmployeeModal from './modals/EditEmployeeModal';
import { useNavigate } from 'react-router-dom'; 
import { useOrganizationContext } from '../../../../context/organization_context/OrganizationContext';
import { TableBody, TableCell, TableContainer, TableHead, TableRow, Table, Switch, useMediaQuery, IconButton } from '@mui/material';
import { updateSubscribedUsers } from '../../../../repository/OrganizationDAO';
import { ToastType, useToast } from '../../../../context/toast/ToastContext';
import { useUserContext } from '../../../../context/user_context/UserContext';
import { useSubscriptionContext } from '../../../../context/subscription_context/SubscriptionContext';
import { IconCaretRightFilled } from '@tabler/icons-react';
import usePermissions from '../../../../utils/permissions/UsePermissions';
import { Modules } from '../../../../utils/permissions/Modules';
import { PermissionTypes } from '../../../../utils/permissions/PermissionTypes';
import { StringKeys } from '../../../../resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';
import CustomPagination from '../../../../components/pagination/CustomPagination';

const EmployeesTable = ({ employees, page, rowsPerPage, handleChangePage, handleChangeRowsPerPage }) => {
  const [employeeToEdit, setEmployeeToEdit] = useState();
  const [showEmployeeToEditModal, setShowEmployeeToEditModal] = useState(false);
  const { subscribedUsers, getDepartmentByID, organization } = useOrganizationContext();
  const { firebaseUser } = useUserContext();
  const { seatsAvailable } = useSubscriptionContext();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const isMobile = useMediaQuery('(max-width:600px)');
  const { hasPermission } = usePermissions();
  const { t } = useTranslation();

  const handleSwitchChange = async (employeeId, employeeRole, checked, isActive) => {
    if (checked && !isActive) {
        showToast(t(StringKeys.USER_MUST_BE_IN_AN_ACTIVE_STATE_TO_ADD_A_SUBSCRIPTION), ToastType.DANGER);
        return;
    }

    if (seatsAvailable > 0 || !checked) {
        const result = await updateSubscribedUsers(firebaseUser.organization, employeeId, employeeRole, checked);

        if (result.success) {
            showToast(
                checked 
                    ? t(StringKeys.SUCCESSFULLY_ADDED_SUBSCRIPTION) 
                    : t(StringKeys.SUCCESSFULLY_REMOVED_SUBSCRIPTION),
                ToastType.SUCCESS
            );
        } else {
            showToast(t(StringKeys.FAILED_TO_UPDATE_SUBSCRIPTION), ToastType.DANGER);
            console.error("Error updating subscription:", result.error);
        }
    } else {
        showToast(t(StringKeys.YOU_HAVE_USED_ALL_OF_YOUR_AVAILABLE_SUBSCRIPTION_SEATS), ToastType.DANGER);
    }
  };

  const handleRowClick = (employeeId) => {
    navigate(`/employees/${employeeId}`);
  };

  return (
    <>
      <TableContainer style={{ overflowX: 'auto' }}>
        <Table aria-label="employee-table">
          <TableHead>
            <TableRow>
              {!isMobile && <TableCell></TableCell>}
              <TableCell style={{ fontWeight: 'bold' }}>{t(StringKeys.NAME)}</TableCell>
              {!isMobile && <TableCell style={{ fontWeight: 'bold' }}>{t(StringKeys.PHONE)}</TableCell>}
              {!isMobile && <TableCell style={{ fontWeight: 'bold' }}>{t(StringKeys.DEPARTMENT)}</TableCell>}
              {!isMobile && (
                <TableCell style={{ fontWeight: 'bold' }}>
                  {t(StringKeys.ROLE)}
                </TableCell>
              )}
              {hasPermission(Modules.EMPLOYEES, PermissionTypes.WRITE) && <TableCell style={{ fontWeight: 'bold' }}>{t(StringKeys.SUBSCRIBED)}</TableCell>}
              {isMobile && <TableCell style={{ fontWeight: 'bold' }}></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {employees.map((employee, index) => (
              <TableRow
                key={index}
                style={{ cursor: !isMobile && 'pointer' }}
                onClick={() => !isMobile && handleRowClick(employee.id)}
              >
                {!isMobile && (
                  <>
                    {employee.profile_picture_url ? (
                      <TableCell width={75}>
                        <img
                          src={employee.profile_picture_url}
                          alt={t(StringKeys.PROFILE)}
                          style={{
                            width: '50px',
                            height: '50px',
                            borderRadius: '50%',
                            objectFit: 'cover'
                          }}
                        />
                      </TableCell>
                    ) : (
                      <TableCell width={75}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: '#007bff', //Can use employee.calendar_color here if we want to
                            color: 'white',
                            fontWeight: 'bold',
                            width: '50px',
                            height: '50px',
                            borderRadius: '50%'
                          }}
                        >
                          {employee.first_name[0].toUpperCase()}
                          {employee.last_name[0].toUpperCase()}
                        </div>
                      </TableCell>
                    )}
                  </>
                )}
                <TableCell>{employee.first_name} {employee.last_name}</TableCell>
                {!isMobile && <TableCell>{employee.phone_number}</TableCell>}
                {!isMobile && 
                  <TableCell>
                    {(() => {
                      const department = getDepartmentByID(employee.department_id);
                      return department ? department.id : t(StringKeys.UNASSIGNED);
                    })()}
                  </TableCell>
                }
                {!isMobile && <TableCell>{employee.role}</TableCell>}

                {hasPermission(Modules.EMPLOYEES, PermissionTypes.WRITE) && (
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {subscribedUsers[employee.id] !== 'Owner' ? (
                      <Switch
                        checked={Object.keys(subscribedUsers).includes(employee.id)}
                        onChange={(e) => handleSwitchChange(employee.id, employee.role, e.target.checked, employee.active)}
                        size="small"
                      />
                    ) : (
                      <div>{t(StringKeys.OWNER)}</div>
                    )}
                  </TableCell>
                )}
                {isMobile && (
                  <TableCell sx={{textAlign:"end"}} onClick={(e) => { e.stopPropagation(); handleRowClick(employee.id); }}>
                    <IconButton>
                      <IconCaretRightFilled />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomPagination
        rowsPerPageOptions={[10, 25, 100, { label: 'All', value: -1 }]}
        component="div"
        count={employees.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <EditEmployeeModal
        employee={employeeToEdit}
        show={showEmployeeToEditModal}
        onHide={() => {
          setShowEmployeeToEditModal(false);
          setEmployeeToEdit();
        }}
      />
    </>
  );
};

export default EmployeesTable;